import React from 'react';
import { useDrag } from 'react-dnd';
import { Button, Checkbox, Tag, message } from 'antd';
import formatDuration from '../../../utils/formatDuration';
import api from '../../../lib/api';
import { notifyTimerChanged } from '../Timer/TimerBar';
import type { PlanTask, Priority } from '../../../types/plan';

export const TASK_DRAG_TYPE = 'TRACKER_TASK';

// 与 /todo 的 PRIORITY_CLASS 刻意不共用：那边是 Tailwind 类名，这边是 antd Tag
// 的 color 名，两页视觉语言不同。色系保持一致（红/琥珀/蓝/灰）。
export const PRIORITY_META: Record<'p1' | 'p2' | 'p3' | 'p4', { label: string; color: string }> = {
  p1: { label: 'P1', color: 'red' },
  p2: { label: 'P2', color: 'gold' },
  p3: { label: 'P3', color: 'blue' },
  p4: { label: 'P4', color: 'default' },
};

export const WORK_TYPE_LABELS: Record<'deep' | 'simple' | 'shallow', string> = {
  deep: '深度',
  simple: '简单',
  shallow: '杂事',
};

export type TaskDragItem = {
  id: number;
  planned_for: string | null;
  important: boolean;
  priority: Priority;
};

type TaskRowProps = {
  task: PlanTask;
  draggable?: boolean;
  // 优先级/项目/面向/精力类型视图里没有可拖拽的"今天/明天"放置目标，用这两个
  // 按钮代替跨视图拖拽,一步完成"挑事"与"排期"。日期视图本来就靠拖拽改
  // planned_for,不需要这两个按钮,所以默认关闭。
  showQuickSchedule?: boolean;
  onQuickSchedule?: (task: PlanTask, when: 'today' | 'tomorrow') => void;
  onToggle: (task: PlanTask) => void;
  onEdit: (task: PlanTask) => void;
};

const TaskRow: React.FC<TaskRowProps> = ({
  task,
  draggable = true,
  showQuickSchedule = false,
  onQuickSchedule,
  onToggle,
  onEdit,
}) => {
  // 父任务的完成态不自动由子任务推导（spec §4.2）——自动完成在「父任务还有收尾
  // 工作」的场景下会误判，所以只提示，勾不勾由用户决定。
  const allSubtasksDone =
    !task.completed && task.subtasks.length > 0 && task.subtasks.every((sub) => sub.completed);

  const spentMinutes = Math.round(task.spent_seconds / 60);
  const estimated = task.estimated_minutes ?? 0;
  const overrun = estimated > 0 && spentMinutes > estimated;
  const percent = estimated > 0 ? Math.min(100, (spentMinutes / estimated) * 100) : 0;
  const deltaMinutes = estimated > 0 ? spentMinutes - estimated : 0;

  const startTimer = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await api.timeEntries.start({ task_id: task.id });
      notifyTimerChanged();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '开始计时失败');
    }
  };

  const [{ isDragging }, drag] = useDrag<TaskDragItem, unknown, { isDragging: boolean }>(
    () => ({
      type: TASK_DRAG_TYPE,
      item: {
        id: task.id,
        planned_for: task.planned_for,
        important: task.important,
        priority: task.priority,
      },
      canDrag: () => draggable,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [task.id, task.planned_for, task.important, task.priority, draggable]
  );

  return (
    <div
      ref={drag as unknown as React.Ref<HTMLDivElement>}
      className="mb-2 rounded-lg border border-gray-200 bg-white p-3 transition-shadow hover:shadow-sm"
      style={{ opacity: isDragging ? 0.4 : 1, cursor: draggable ? 'grab' : 'default' }}
    >
      <div className="flex items-start gap-2">
        <Checkbox
          checked={task.completed}
          onChange={() => onToggle(task)}
          onClick={(event) => event.stopPropagation()}
        />

        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => onEdit(task)}
        >
          <span
            className={`block truncate text-sm ${
              task.completed ? 'text-gray-400 line-through' : 'text-gray-900'
            }`}
          >
            {task.title}
          </span>
        </button>

        <div className="flex-none text-right">
          <div className="text-xs tabular-nums text-gray-600">
            {formatDuration(spentMinutes)}
            {estimated > 0 && <span className="text-gray-400"> / {formatDuration(estimated)}</span>}
          </div>
          {estimated > 0 && spentMinutes > 0 && (
            <div className={`text-xs tabular-nums ${overrun ? 'text-red-600' : 'text-gray-400'}`}>
              {deltaMinutes >= 0 ? '+' : '−'}
              {formatDuration(Math.abs(deltaMinutes))}
            </div>
          )}
        </div>

        <Button size="small" type="text" onClick={startTimer}>
          计时
        </Button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1 pl-6">
        <Tag
          bordered={false}
          className="!mr-0"
          icon={
            <span
              className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
              style={{ background: task.project?.color ?? '#bfbfbf' }}
            />
          }
        >
          {task.project?.name ?? '未归项目'}
        </Tag>

        {/* nil 不渲染。换成 priority 之前每条任务都必然有个象限 Tag（两个 not-null
            布尔推出来的，默认「Q4 别做」），所以这一页看起来会明显变空 —— 那是
            预期：「从没标过」和「想过，就是最低」终于分开了。别修回去。 */}
        {task.priority && (
          <Tag bordered={false} color={PRIORITY_META[task.priority].color} className="!mr-0">
            {PRIORITY_META[task.priority].label}
          </Tag>
        )}

        {task.work_type && (
          <Tag bordered={false} color="purple" className="!mr-0">
            {WORK_TYPE_LABELS[task.work_type]}
          </Tag>
        )}

        {task.category && (
          <Tag bordered={false} className="!mr-0">
            {task.category}
          </Tag>
        )}
      </div>

      {estimated > 0 && (
        <div className="mt-2 h-1 overflow-hidden rounded bg-gray-100">
          <div
            className={`h-full rounded ${overrun ? 'bg-red-400' : 'bg-blue-500'}`}
            style={{ width: `${overrun ? 100 : percent}%` }}
          />
        </div>
      )}

      {showQuickSchedule && onQuickSchedule && (
        <div className="mt-2 flex gap-3 pl-6">
          <Button
            type="link"
            size="small"
            className="!h-auto !p-0 text-xs"
            onClick={(event) => {
              event.stopPropagation();
              onQuickSchedule(task, 'today');
            }}
          >
            排到今天
          </Button>
          <Button
            type="link"
            size="small"
            className="!h-auto !p-0 text-xs"
            onClick={(event) => {
              event.stopPropagation();
              onQuickSchedule(task, 'tomorrow');
            }}
          >
            排到明天
          </Button>
        </div>
      )}

      {task.subtasks.length > 0 && (
        <ul className="mt-2 space-y-1 border-l border-gray-100 pl-8">
          {task.subtasks.map((subtask) => (
            <li key={subtask.id} className="flex items-center gap-2 text-xs">
              <Checkbox checked={subtask.completed} onChange={() => onToggle(subtask)} />
              <span className={subtask.completed ? 'text-gray-400 line-through' : 'text-gray-600'}>
                {subtask.title}
              </span>
              <span className="ml-auto tabular-nums text-gray-400">
                {formatDuration(Math.round(subtask.spent_seconds / 60))}
              </span>
            </li>
          ))}
        </ul>
      )}

      {allSubtasksDone && (
        <div className="mt-2 pl-8 text-xs text-blue-600">
          子任务都完成了，要勾掉这个任务吗？
        </div>
      )}
    </div>
  );
};

export default TaskRow;
